import React, { useState } from 'react';
import { PublicHeader } from '../components/layout/PublicHeader';
import { PublicFooter } from '../components/layout/PublicFooter';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import { Button } from '@mui/joy';
import api from '../services/api';

export const DeleteData: React.FC = () => {
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [service, setService] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        try {
            await api.post('/delete-data/', {
                email,
                username,
                service
            });
            setSubmitted(true);
        } catch (err) {
            setError('Hubo un error al procesar tu solicitud. Por favor intenta nuevamente.');
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-background-light dark:bg-background-dark min-h-screen flex flex-col transition-colors duration-200 font-body">
            <PublicHeader />

            <div className="flex-1 w-full max-w-[1400px] mx-auto px-4 sm:px-10 pt-28 pb-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 relative">
                    {/* Sidebar */}
                    <aside className="hidden lg:block lg:col-span-3">
                        <div className="sticky top-24 rounded-xl bg-white dark:bg-[#1a2332] border border-gray-200 dark:border-[#282e39] p-6 shadow-sm">
                            <div className="mb-6">
                                <h3 className="text-slate-900 dark:text-white text-lg font-bold font-display">Navegación</h3>
                                <p className="text-slate-500 dark:text-gray-400 text-xs mt-1">Secciones</p>
                            </div>
                            <nav className="flex flex-col gap-1 max-h-[calc(100vh-200px)] overflow-y-auto custom-scrollbar">
                                <a href="#rights" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-background-dark/50 hover:text-slate-900 dark:hover:text-white transition-colors">
                                    <span className="material-symbols-outlined text-[20px]">gavel</span>
                                    <span className="text-sm">Tus Derechos</span>
                                </a>
                                <a href="#process" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-background-dark/50 hover:text-slate-900 dark:hover:text-white transition-colors">
                                    <span className="material-symbols-outlined text-[20px]">list_alt</span>
                                    <span className="text-sm">Proceso</span>
                                </a>
                                <a href="#request-form" className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-primary/10 text-primary font-medium transition-colors">
                                    <span className="material-symbols-outlined text-[20px]">assignment</span>
                                    <span className="text-sm">Solicitud</span>
                                </a>
                            </nav>
                        </div>
                    </aside>

                    {/* Main Content */}
                    <main className="lg:col-span-9 flex flex-col gap-8">
                        <div className="bg-white dark:bg-[#1a2332] rounded-xl p-8 border border-gray-200 dark:border-[#282e39] shadow-sm">
                            <div className="flex flex-col gap-4">
                                <div className="flex items-center gap-2 text-primary font-bold text-sm tracking-wider uppercase">
                                    <span className="material-symbols-outlined text-lg">privacy_tip</span>
                                    <span>Privacidad y Datos</span>
                                </div>
                                <h1 className="text-slate-900 dark:text-white text-4xl sm:text-5xl font-bold font-display tracking-tight leading-tight">Eliminación de Datos</h1>
                                <p className="text-slate-600 dark:text-[#9da6b9] text-lg max-w-2xl leading-relaxed">
                                    Gestiona tu privacidad y ejerce tu derecho a la eliminación de tus datos personales.
                                </p>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-[#1a2332] rounded-xl p-8 border border-gray-200 dark:border-[#282e39] shadow-sm flex flex-col gap-12">

                            {/* Section 1: Tus Derechos */}
                            <section className="scroll-mt-28" id="rights">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="bg-primary/10 p-2 rounded-lg text-primary">
                                        <span className="material-symbols-outlined">gavel</span>
                                    </div>
                                    <h2 className="text-slate-900 dark:text-white text-2xl font-bold font-display">1. Tu Derecho a la Eliminación</h2>
                                </div>
                                <div className="space-y-6 text-slate-600 dark:text-gray-300 leading-7">
                                    <p>
                                        De acuerdo con la Ley N° 29733 (Ley de Protección de Datos Personales de Perú) y regulaciones internacionales como el GDPR, tienes derecho a solicitar la eliminación de tus datos personales de nuestros sistemas ("Derecho al Olvido").
                                    </p>
                                    <p>
                                        Este derecho puede ejercerse cuando:
                                    </p>
                                    <ul className="list-disc pl-5 space-y-1">
                                        <li>Los datos ya no sean necesarios para los fines para los que fueron recogidos.</li>
                                        <li>Retires tu consentimiento y no exista otro fundamento jurídico para el tratamiento.</li>
                                        <li>Te opongas al tratamiento y no prevalezcan otros motivos legítimos.</li>
                                        <li>Los datos hayan sido tratados ilícitamente.</li>
                                    </ul>
                                </div>
                            </section>

                            <hr className="border-gray-200 dark:border-border-dark" />

                            {/* Section 2: Proceso */}
                            <section className="scroll-mt-28" id="process">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="bg-primary/10 p-2 rounded-lg text-primary">
                                        <span className="material-symbols-outlined">list_alt</span>
                                    </div>
                                    <h2 className="text-slate-900 dark:text-white text-2xl font-bold font-display">2. Proceso de Solicitud</h2>
                                </div>
                                <div className="space-y-6 text-slate-600 dark:text-gray-300 leading-7">
                                    <p>
                                        Para procesar tu solicitud de manera segura y eficiente, sigue estos pasos:
                                    </p>
                                    <ol className="list-decimal pl-5 space-y-2">
                                        <li><strong>Completa el Formulario:</strong> Ingresa tu correo electrónico, usuario (si aplica) y el servicio del cual deseas eliminar tus datos en el formulario a continuación.</li>
                                        <li><strong>Verificación de Identidad:</strong> Recibirás un correo electrónico de confirmación. Es posible que nuestro equipo te contacte para verificar tu identidad y asegurar que la solicitud es legítima, evitando suplantaciones.</li>
                                        <li><strong>Procesamiento:</strong> Una vez verificada tu identidad, procederemos con la eliminación de tus datos en un plazo máximo de 10 días hábiles, salvo que existan obligaciones legales que nos impidan hacerlo (ej. facturación, registros de auditoría obligatorios).</li>
                                        <li><strong>Confirmación Final:</strong> Recibirás una notificación final confirmando la eliminación o anonimización de tus datos.</li>
                                    </ol>
                                </div>
                            </section>

                            <hr className="border-gray-200 dark:border-border-dark" />

                            {/* Section 3: Formulario */}
                            <section className="scroll-mt-28" id="request-form">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="bg-primary/10 p-2 rounded-lg text-primary">
                                        <span className="material-symbols-outlined">assignment</span>
                                    </div>
                                    <h2 className="text-slate-900 dark:text-white text-2xl font-bold font-display">3. Formulario de Solicitud</h2>
                                </div>

                                {submitted ? (
                                    <div className="bg-green-50 dark:bg-green-900/10 rounded-xl p-8 border border-green-200 dark:border-green-800 text-center">
                                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-800/30 mb-6">
                                            <ShieldCheckIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                                        </div>
                                        <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Solicitud Recibida</h3>
                                        <p className="text-slate-600 dark:text-gray-300 mb-6">
                                            Hemos recibido tu solicitud de eliminación de datos. Procederemos a verificar la información y enviaremos un correo de confirmación a <strong>{email}</strong> con los pasos siguientes.
                                        </p>
                                        <Button color="primary" variant="solid" onClick={() => window.location.href = '/'}>
                                            Volver al Inicio
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="bg-gray-50 dark:bg-[#111318] p-8 rounded-xl border border-gray-200 dark:border-[#282e39]">
                                        <form onSubmit={handleSubmit} className="space-y-6">
                                            <div>
                                                <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">
                                                    Correo Electrónico
                                                </label>
                                                <input
                                                    type="email"
                                                    id="email"
                                                    required
                                                    className="w-full px-4 py-3 bg-white dark:bg-[#1a2332] border border-gray-200 dark:border-[#282e39] rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 transition-all outline-none"
                                                    placeholder="tu@email.com"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                />
                                            </div>

                                            <div>
                                                <label htmlFor="username" className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">
                                                    Usuario (Opcional)
                                                </label>
                                                <input
                                                    type="text"
                                                    id="username"
                                                    className="w-full px-4 py-3 bg-white dark:bg-[#1a2332] border border-gray-200 dark:border-[#282e39] rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 transition-all outline-none"
                                                    placeholder="Nombre de usuario"
                                                    value={username}
                                                    onChange={(e) => setUsername(e.target.value)}
                                                />
                                            </div>

                                            <div>
                                                <label htmlFor="service" className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">
                                                    Servicio
                                                </label>
                                                <input
                                                    type="text"
                                                    id="service"
                                                    required
                                                    className="w-full px-4 py-3 bg-white dark:bg-[#1a2332] border border-gray-200 dark:border-[#282e39] rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 transition-all outline-none"
                                                    placeholder="Ej: Plataforma de Seguridad, Consultoría..."
                                                    value={service}
                                                    onChange={(e) => setService(e.target.value)}
                                                />
                                            </div>

                                            {error && (
                                                <div className="p-4 rounded-lg bg-red-50 text-red-600 border border-red-200 text-sm">
                                                    {error}
                                                </div>
                                            )}

                                            <div className="pt-4">
                                                <Button
                                                    type="submit"
                                                    loading={isSubmitting}
                                                    fullWidth
                                                    size="lg"
                                                    color="primary"
                                                    variant="solid"
                                                >
                                                    Enviar Solicitud
                                                </Button>
                                            </div>

                                            <p className="text-xs text-center text-slate-500 dark:text-gray-400 mt-4">
                                                Al enviar esta solicitud, aceptas que procesemos tus datos con el único fin de gestionar su eliminación conforme a nuestra Política de Privacidad y la normativa vigente.
                                            </p>
                                        </form>
                                    </div>
                                )}
                            </section>
                        </div>
                    </main>
                </div>
            </div>

            <PublicFooter />
        </div>
    );
};
